import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { debounce } from 'lodash';

import { Trajectory } from './useDpdgState';

interface IBallAnimationProps {
  canvasRef: React.RefObject<HTMLCanvasElement>;
  moving: boolean;
  velocity: number;
  trajectory: Trajectory;
  ballColor: string;
  bgColor: string;
  backgroundType: 'none' | 'video' | 'image';
  ballSize: number;
}

export const useBallAnimation = ({
  canvasRef,
  moving,
  velocity,
  trajectory,
  ballColor,
  bgColor,
  backgroundType,
  ballSize,
}: IBallAnimationProps) => {
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  const positionRef = useRef({ x: 0, y: 0 });
  const animationFrameRef = useRef<number>();
  const progressRef = useRef(0);
  const directionRef = useRef<1 | -1>(1);
  const lastTimestampRef = useRef<number | null>(null);

  const updateSizeNow = useCallback(() => {
    const canvas = canvasRef.current;
    const parent = canvas?.parentElement;

    if (canvas && parent) {
      const { width, height } = parent.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;

      setContainerSize((prev) => {
        if (Math.abs(prev.width - width) < 1 && Math.abs(prev.height - height) < 1) {
          return prev;
        }

        canvas.width = width * dpr;
        canvas.height = height * dpr;
        canvas.style.width = `${width}px`;
        canvas.style.height = `${height}px`;

        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.setTransform(1, 0, 0, 1, 0, 0);
          ctx.scale(dpr, dpr);
        }

        return { width, height };
      });
    }
  }, [canvasRef]);

  const updateSize = useMemo(() => debounce(updateSizeNow, 300), [updateSizeNow]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const parent = canvas?.parentElement;
    let resizeObserver: ResizeObserver | null = null;

    updateSizeNow();
    window.addEventListener('resize', updateSize);
    if (parent && typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => updateSizeNow());
      resizeObserver.observe(parent);
    }

    return () => {
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      window.removeEventListener('resize', updateSize);
      updateSize.cancel();
    };
  }, [canvasRef, updateSize, updateSizeNow]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || containerSize.width === 0 || containerSize.height === 0) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const maxX = Math.max(containerSize.width - ballSize, 0);
    const maxY = Math.max(containerSize.height - ballSize, 0);
    const centerX = Math.max((containerSize.width - ballSize) / 2, 0);
    const centerY = Math.max((containerSize.height - ballSize) / 2, 0);
    const diagonalDistance = Math.sqrt(maxX * maxX + maxY * maxY);
    const circleRadius = Math.min(maxX, maxY) / 2;
    const closedTrajectoryLength = Math.max(maxX, maxY, ballSize) * 4;
    const isClosedTrajectory = ['circle', 'eight-vertical', 'eight-horizontal', 'cross'].includes(trajectory);
    const crossTrajectoryLength = maxX * 2 + maxY * 2;

    const limit =
      trajectory === 'horizontal'
        ? maxX
        : trajectory === 'vertical'
        ? maxY
        : trajectory === 'circle'
        ? Math.max(2 * Math.PI * circleRadius, 0)
        : trajectory === 'cross'
        ? crossTrajectoryLength
        : isClosedTrajectory
        ? closedTrajectoryLength
        : diagonalDistance;

    progressRef.current = 0;
    directionRef.current = 1;
    lastTimestampRef.current = null;

    const drawBall = (x: number, y: number) => {
      const centerXBall = x + ballSize / 2;
      const centerYBall = y + ballSize / 2;

      const gradient = ctx.createRadialGradient(
        centerXBall,
        centerYBall,
        ballSize * 0.05,
        centerXBall,
        centerYBall,
        ballSize / 2,
      );
      gradient.addColorStop(0, 'white');
      gradient.addColorStop(1, ballColor);

      ctx.beginPath();
      ctx.arc(centerXBall, centerYBall, ballSize / 2, 0, Math.PI * 2);
      ctx.fillStyle = gradient;
      ctx.fill();
    };

    const renderScene = (x: number, y: number) => {
      ctx.clearRect(0, 0, containerSize.width, containerSize.height);

      if (backgroundType === 'none') {
        ctx.fillStyle = bgColor;
        ctx.fillRect(0, 0, containerSize.width, containerSize.height);
      }

      drawBall(x, y);
    };

    const getCrossPosition = (progress: number) => {
      let distance = crossTrajectoryLength > 0 ? progress % crossTrajectoryLength : 0;

      if (distance <= maxX) {
        return { x: distance, y: centerY };
      }

      distance -= maxX;

      if (distance <= maxX) {
        return { x: maxX - distance, y: centerY };
      }

      distance -= maxX;

      if (distance <= maxY) {
        return { x: centerX, y: distance };
      }

      distance -= maxY;

      return { x: centerX, y: maxY - distance };
    };

    const getPositionFromProgress = (progress: number) => {
      if (trajectory === 'cross') {
        return getCrossPosition(progress);
      }

      if (isClosedTrajectory) {
        const phase = limit > 0 ? ((progress % limit) / limit) * Math.PI * 2 : 0;

        if (trajectory === 'circle') {
          return {
            x: centerX + circleRadius * Math.cos(phase),
            y: centerY + circleRadius * Math.sin(phase),
          };
        }

        if (trajectory === 'eight-vertical') {
          return {
            x: centerX + (maxX / 2) * Math.sin(phase * 2),
            y: centerY + (maxY / 2) * Math.sin(phase),
          };
        }

        return {
          x: centerX + (maxX / 2) * Math.sin(phase),
          y: centerY + (maxY / 2) * Math.sin(phase * 2),
        };
      }

      const clampedProgress = limit > 0 ? Math.min(Math.max(progress, 0), limit) : 0;

      if (trajectory === 'horizontal') {
        return { x: clampedProgress, y: centerY };
      }

      if (trajectory === 'vertical') {
        return { x: centerX, y: clampedProgress };
      }

      const ratio = limit === 0 ? 0 : clampedProgress / limit;

      if (trajectory === 'diagonal-bl-tr') {
        return { x: ratio * maxX, y: maxY - ratio * maxY };
      }

      return { x: maxX - ratio * maxX, y: maxY - ratio * maxY };
    };

    const initialPosition = getPositionFromProgress(progressRef.current);
    positionRef.current = initialPosition;

    const animate = (time: number) => {
      const previousTimestamp = lastTimestampRef.current;
      lastTimestampRef.current = time;

      let progress = progressRef.current;
      let direction = directionRef.current;

      if (previousTimestamp !== null && velocity > 0 && limit > 0) {
        const deltaSeconds = Math.min((time - previousTimestamp) / 1000, 0.1);
        const speedPerSecond = velocity * 1000;
        let nextProgress = progress + speedPerSecond * deltaSeconds * direction;

        if (isClosedTrajectory) {
          nextProgress %= limit;
        } else {
          // Reflect progress at the boundaries so the ball bounces smoothly.
          while (nextProgress > limit || nextProgress < 0) {
            if (nextProgress > limit) {
              nextProgress = limit - (nextProgress - limit);
            } else {
              nextProgress = -nextProgress;
            }
            direction = (direction === 1 ? -1 : 1);
          }
        }

        progress = nextProgress;
      }

      const { x, y } = getPositionFromProgress(progress);

      progressRef.current = progress;
      directionRef.current = direction;
      positionRef.current = { x, y };

      renderScene(x, y);

      if (moving && velocity > 0 && limit > 0) {
        animationFrameRef.current = requestAnimationFrame(animate);
      }
    };

    renderScene(initialPosition.x, initialPosition.y);

    if (moving && velocity > 0 && limit > 0) {
      animationFrameRef.current = requestAnimationFrame(animate);
    }

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = undefined;
      }
    };
  }, [canvasRef, moving, velocity, trajectory, ballColor, bgColor, containerSize, backgroundType, ballSize]);

  return { containerSize, updateSize };
};
